import {
  cardDividerBorderClass,
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
  compactInputClass,
} from "@/components/common/surfaceClasses";
import {
  createAdsPolicy,
  deleteAdsPolicy,
  type AdsAssistantPolicyRow,
} from "@/lib/adsAssistantClient";
import { linesToPatterns } from "./adsLibrarySectionShared";

export function AdsLibraryPoliciesPanel(props: {
  canEdit: boolean;
  policies: AdsAssistantPolicyRow[];
  polTitle: string;
  setPolTitle: (v: string) => void;
  polBody: string;
  setPolBody: (v: string) => void;
  polPatterns: string;
  setPolPatterns: (v: string) => void;
  polSeverity: "info" | "warning" | "critical";
  setPolSeverity: (v: "info" | "warning" | "critical") => void;
  setError: (e: string | null) => void;
  setBanner: (b: string | null) => void;
  load: () => Promise<void>;
}) {
  const {
    canEdit,
    policies,
    polTitle,
    setPolTitle,
    polBody,
    setPolBody,
    polPatterns,
    setPolPatterns,
    polSeverity,
    setPolSeverity,
    setError,
    setBanner,
    load,
  } = props;

  return (
    <div className={`${cardSurfaceClass} p-3`}>
      <h4 className={`text-xs font-semibold uppercase tracking-wide ${cardMutedTextClass}`}>Policies &amp; detection</h4>
      <ul className="mt-2 max-h-36 space-y-2 overflow-y-auto text-sm">
        {policies.map((p) => (
          <li key={p.id} className={`rounded border px-2 py-1.5 ${cardDividerBorderClass} dark:bg-zinc-900/40`}>
            <div className="flex flex-wrap items-start justify-between gap-1">
              <span className={`font-medium ${cardTitleTextClass}`}>
                {p.title} <span className={`text-[10px] font-normal ${cardMutedTextClass}`}>({p.severity})</span>
              </span>
              {canEdit ? (
                <button
                  type="button"
                  className="text-[10px] text-red-700 hover:underline dark:text-red-400"
                  onClick={async () => {
                    const r = await deleteAdsPolicy(p.id);
                    if (!r.ok) {
                      setError(r.error);
                      return;
                    }
                    setBanner("Policy removed.");
                    void load();
                  }}
                >
                  Remove
                </button>
              ) : null}
            </div>
            <p className={`text-[11px] line-clamp-2 ${cardMutedTextClass}`}>{p.body_markdown || "—"}</p>
          </li>
        ))}
        {!policies.length ? <li className={`text-xs ${cardMutedTextClass}`}>No policies yet.</li> : null}
      </ul>
      {canEdit ? (
        <div className={`mt-3 space-y-2 border-t pt-3 ${cardDividerBorderClass}`}>
          <input
            className={`w-full ${compactInputClass}`}
            placeholder="Policy title"
            value={polTitle}
            onChange={(e) => setPolTitle(e.target.value)}
          />
          <textarea
            className={`w-full min-h-[52px] ${compactInputClass}`}
            placeholder="Guideline text (for people)"
            value={polBody}
            onChange={(e) => setPolBody(e.target.value)}
          />
          <textarea
            className={`w-full font-mono min-h-[56px] ${compactInputClass}`}
            placeholder="Substring hints — one per line (stub flags post if text contains)"
            value={polPatterns}
            onChange={(e) => setPolPatterns(e.target.value)}
          />
          <select
            className={`w-full ${compactInputClass}`}
            value={polSeverity}
            onChange={(e) => setPolSeverity(e.target.value as typeof polSeverity)}
          >
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
          <button
            type="button"
            className="w-full rounded-lg bg-blue-700 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-800"
            onClick={async () => {
              const r = await createAdsPolicy({
                title: polTitle.trim(),
                body_markdown: polBody,
                detection_patterns: linesToPatterns(polPatterns),
                severity: polSeverity,
              });
              if (!r.ok) {
                setError(r.error);
                return;
              }
              setPolTitle("");
              setPolBody("");
              setPolPatterns("");
              setPolSeverity("warning");
              setBanner("Policy added.");
              void load();
            }}
          >
            Add policy
          </button>
        </div>
      ) : null}
    </div>
  );
}
